// LeadAcquisition.jsx — landing for the Lead Acquisition package.
// Lives at /lead-acquisition/:tab? — deep-linkable like SeoManagement.
//
// Three tabs (mirrors the SEO/Chat tabbed page UI style):
//   1. Crawler  — pick a brand, run the crawler, watch a live table of runs
//                 (time running · sources checked · signals used · leads collected).
//   2. Leads    — every collected lead (name / email / phone / source(s) /
//                 last active / most present on / status warm·cold·green).
//   3. Lists    — build a list from the collected leads, filtered by source /
//                 status / last-active / location. Creating a list pushes the
//                 leads into the CRM and mirrors them into an Email automations
//                 list (so the segment shows in the Email automations dropdown).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { get, post, putGeneral } from "../../api";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import SystemFilterBar from "components/SystemFilterBar";
import { applySystemFilter, EMPTY_SYSTEM_FILTERS } from "../../helpers/systemFilters";
import useBrandList from "../../hooks/useBrandList";

const TABS = [
  { key: 'contact-forms', label: 'Contact forms', endpoint: '/api/lead-acquisition/forms/requests', perm: 'LEAD_CRAWLER_VIEW', custom: true },
  { key: 'meetings',      label: 'Meetings booking', endpoint: '/api/lead-acquisition/forms/meetings', perm: 'LEAD_CRAWLER_VIEW', custom: true },
  { key: 'crawler', label: 'Crawler',  endpoint: '/api/lead-acquisition/runs',  perm: 'LEAD_CRAWLER_VIEW' },
  { key: 'leads',   label: 'Leads',    endpoint: '/api/lead-acquisition/leads', perm: 'LEAD_VIEW' },
  { key: 'lists',   label: 'Lists',    endpoint: '/api/lead-acquisition/lists', perm: 'LEAD_LIST_VIEW' },
];

const LEAD_SOURCES = ['linkedin', 'twitter', 'reddit', 'quora', 'github', 'indiehackers', 'stackexchange', 'producthunt', 'domain-whois', 'newsletter'];

function getPath(obj, path) { return path.split('.').reduce((o, k) => (o == null ? null : o[k]), obj); }
function fmtCell(v) {
  if (v == null || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '—';
  if (typeof v === 'boolean') return v ? '✓' : '—';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) { try { return new Date(v).toLocaleString(); } catch (e) {} }
  if (typeof v === 'string' && v.length > 60) return v.slice(0, 60) + '…';
  return String(v);
}
function fmtDuration(ms) {
  if (!ms || ms < 0) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function statusBadge(status) {
  const map = {
    green:     { bg: '#1e6b3e', fg: '#fff' },
    warm:      { bg: '#fff4cc', fg: '#856404' },
    cold:      { bg: '#eef2ff', fg: '#15172b' },
    running:   { bg: '#fff4cc', fg: '#856404' },
    completed: { bg: '#e7f5ec', fg: '#1e6b3e' },
    failed:    { bg: '#fde8e8', fg: '#a71d2a' },
  };
  const s = map[status] || { bg: '#fafbfc', fg: '#525252' };
  return <span style={{ display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 11, background: s.bg, color: s.fg, textTransform: 'capitalize' }}>{status || '—'}</span>;
}

function Modal({ title, onClose, children, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div style={{ background: '#fff', width: wide ? 760 : 520, maxWidth: '95vw', maxHeight: '90vh', overflow: 'auto', borderRadius: 8, padding: 22 }} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-baseline justify-content-between mb-3">
          <h5 className="mb-0" style={{ wordBreak: 'break-word', paddingRight: 12 }}>{title}</h5>
          <button className="acc-btn" onClick={onClose}>Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────── Run crawler (tab 1)
function RunCrawler({ brands, onRan }) {
  const [brandId, setBrandId] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const run = async () => {
    if (!brandId) { setErr('Pick a brand to crawl.'); return; }
    setBusy(true); setErr(null);
    try {
      await post('/api/lead-acquisition/runs/crawl', { brand_id: brandId }, false);
      onRan && onRan();
    } catch (e) { setErr((e && (e.message || e.error)) || 'Could not start crawl'); }
    finally { setBusy(false); }
  };
  return (
    <div className="sm-card" style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <div style={{ fontSize: 12, color: '#525252', flexShrink: 0 }}>Run crawler for brand:</div>
      <select className="form-control" value={brandId} onChange={(e) => setBrandId(e.target.value)} style={{ flex: 1, minWidth: 180, fontSize: 13 }}>
        <option value="">— pick brand —</option>
        {brands.map((b) => (<option key={b._id} value={b._id}>{b.name}</option>))}
      </select>
      <button className="acc-btn acc-btn-primary" onClick={run} disabled={busy || !brandId}>{busy ? 'Starting…' : 'Run crawler'}</button>
      <div style={{ fontSize: 11, color: '#888' }}>Collects all brand settings/signals (domain · social interests · forum subscriptions) and crawls public sources.</div>
      {err && <div className="alert alert-warning w-100 mb-0" style={{ fontSize: 13 }}>{err}</div>}
    </div>
  );
}

// ──────────────────────────────────────────────────── Build a list (tab 3)
function CreateListForm({ brands, leads, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', description: '', brand_id: '', source: '', status: '', lastActiveDays: '', location: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Live preview of how many collected leads match the filter.
  const matchCount = useMemo(() => {
    const now = Date.now();
    return leads.filter((l) => {
      if (form.brand_id && l.brand_id !== form.brand_id) return false;
      if (form.source && !(l.sources || []).includes(form.source)) return false;
      if (form.status && l.status !== form.status) return false;
      if (form.location && !String(l.location || '').toLowerCase().includes(form.location.toLowerCase())) return false;
      if (form.lastActiveDays) {
        const days = Number(form.lastActiveDays);
        if (!l.lastActiveAt || (now - new Date(l.lastActiveAt).getTime()) > days * 86400000) return false;
      }
      return true;
    }).length;
  }, [leads, form]);

  const submit = async () => {
    if (!form.name.trim()) { setErr('Give the list a name.'); return; }
    setBusy(true); setErr(null);
    try {
      const body = {
        name: form.name, description: form.description, brand_id: form.brand_id || null,
        source: form.source || undefined, status: form.status || undefined,
        lastActiveDays: form.lastActiveDays || undefined, location: form.location || undefined,
      };
      await post('/api/lead-acquisition/lists', body, false);
      onCreated && onCreated();
    } catch (e) { setErr((e && (e.message || e.error)) || 'Could not create list'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="New lead list" onClose={onClose}>
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}
      <label style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>Name *
        <input className="form-control" value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Warm trail-runners — Q3" />
      </label>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>Description
        <input className="form-control" value={form.description} onChange={(e) => set('description', e.target.value)} />
      </label>
      <div style={{ fontSize: 11, color: '#888', margin: '10px 0 6px', fontWeight: 600 }}>Filter the collected leads</div>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>Brand
        <select className="form-control" value={form.brand_id} onChange={(e) => set('brand_id', e.target.value)}>
          <option value="">— any brand —</option>
          {brands.map((b) => (<option key={b._id} value={b._id}>{b.name}</option>))}
        </select>
      </label>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>Source
        <select className="form-control" value={form.source} onChange={(e) => set('source', e.target.value)}>
          <option value="">— any source —</option>
          {LEAD_SOURCES.map((s) => (<option key={s} value={s}>{s}</option>))}
        </select>
      </label>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>Status
        <select className="form-control" value={form.status} onChange={(e) => set('status', e.target.value)}>
          <option value="">— any status —</option>
          <option value="green">green</option>
          <option value="warm">warm</option>
          <option value="cold">cold</option>
        </select>
      </label>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>Active within (days)
        <input type="number" className="form-control" value={form.lastActiveDays} onChange={(e) => set('lastActiveDays', e.target.value)} placeholder="30" />
      </label>
      <label style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>Location contains
        <input className="form-control" value={form.location} onChange={(e) => set('location', e.target.value)} placeholder="London" />
      </label>
      <div style={{ fontSize: 12, color: '#1e6b3e', margin: '6px 0' }}>{matchCount} collected lead{matchCount === 1 ? '' : 's'} match — they'll be pushed to the CRM and exposed as an Email automations list.</div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
        <button className="acc-btn" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="acc-btn acc-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Creating…' : 'Create list'}</button>
      </div>
    </Modal>
  );
}

// ──────────────────────────────────────────────────── Page
// ─────────────────────────────────── Contact forms (chat-inbox style tab)
// Left: the stream of submissions for the chosen brand. Right: the selected
// submission's detail + the widget config (enable, appointments URL, services)
// and the copy-paste embed snippet.
function ContactFormsTab({ brands }) {
  const [brandId, setBrandId] = useState('');
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { if (!brandId && brands && brands.length) setBrandId(String(brands[0]._id || brands[0].id)); }, [brands, brandId]);

  const load = useCallback(async () => {
    if (!brandId) return;
    setBusy(true);
    try {
      const [r, c] = await Promise.all([
        get(`/api/lead-acquisition/forms/requests?brand_id=${encodeURIComponent(brandId)}`),
        get(`/api/lead-acquisition/forms/config?brand_id=${encodeURIComponent(brandId)}`),
      ]);
      const list = (r && r.data) || [];
      setRows(list); setSel(list[0] || null);
      setCfg((c && c.data) || {});
    } catch (e) { setRows([]); setCfg({}); }
    finally { setBusy(false); }
  }, [brandId]);
  useEffect(() => { load(); }, [load]);

  const saveCfg = async (patch) => {
    setSaving(true);
    try {
      const r = await putGeneral('/api/lead-acquisition/forms/config', { brand_id: brandId, ...cfg, ...patch });
      if (r && r.data) setCfg(r.data);
    } catch (e) { /* surfaced by disabled state resetting */ }
    finally { setSaving(false); }
  };

  const snippet = cfg && cfg.widgetKey
    ? `<script src="${window.location.origin}/lead-widget.js" data-widget-key="${cfg.widgetKey}" async></script>`
    : '';

  return (
    <div>
      <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
        <label style={{ fontSize: 12, color: '#666', margin: 0 }}>Brand</label>
        <select className="form-control" style={{ maxWidth: 240, fontSize: 13 }} value={brandId} onChange={(e) => setBrandId(e.target.value)}>
          {(brands || []).map((b) => (<option key={b._id || b.id} value={b._id || b.id}>{b.name || b.brandName}</option>))}
        </select>
        <button className="acc-btn" onClick={load} disabled={busy} style={{ fontSize: 12 }}>{busy ? '…' : 'Refresh'}</button>
      </div>

      <div className="row">
        {/* Inbox list */}
        <div className="col-md-5">
          <div className="sm-card" style={{ padding: 0, maxHeight: 460, overflowY: 'auto' }}>
            {busy && rows.length === 0 && <div className="sm-empty">Loading…</div>}
            {!busy && rows.length === 0 && <div className="sm-empty">No submissions yet.</div>}
            {rows.map((r) => (
              <div key={r._id} onClick={() => setSel(r)}
                style={{ padding: '10px 12px', borderBottom: '1px solid #f4f4f4', cursor: 'pointer', background: sel && sel._id === r._id ? '#eef4ff' : '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <strong style={{ fontSize: 13 }}>{r.name || r.email || '(no name)'}</strong>
                  <span style={{ fontSize: 11 }}>{statusBadge(r.status)}</span>
                </div>
                <div style={{ fontSize: 12, color: '#555' }}>{r.service || '—'}</div>
                <div style={{ fontSize: 11, color: '#999' }}>{r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Detail + config */}
        <div className="col-md-7">
          {sel && (
            <div className="sm-card mb-3">
              <h6 style={{ fontSize: 13, fontWeight: 700 }}>{sel.name || '(no name)'} · {statusBadge(sel.status)}</h6>
              <div style={{ fontSize: 13, lineHeight: 1.9 }}>
                <div>✉️ {sel.email || '—'}{sel.phone ? ` · 📞 ${sel.phone}` : ''}</div>
                <div>🎯 <strong>{sel.service || '—'}</strong></div>
                {sel.company_name && <div>🏢 {sel.company_name}</div>}
                {sel.message && <div style={{ background: '#f7f7f9', padding: 8, borderRadius: 6, marginTop: 4 }}>{sel.message}</div>}
                {sel.pageUrl && <div style={{ fontSize: 11, color: '#999' }}>from {sel.pageUrl}</div>}
                {sel.bookingUrl && <div><a href={sel.bookingUrl} target="_blank" rel="noreferrer">Open booking link ↗</a></div>}
                {sel.appointmentAt && <div>📅 Booked for {new Date(sel.appointmentAt).toLocaleString()}</div>}
              </div>
            </div>
          )}

          <div className="sm-card">
            <h6 style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Widget settings</h6>
            {cfg && (
              <div style={{ fontSize: 13 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <input type="checkbox" checked={!!cfg.enabled} disabled={saving} onChange={(e) => saveCfg({ enabled: e.target.checked })} />
                  Enable the lead form for this brand
                </label>
                <label style={{ fontSize: 12, color: '#666' }}>Appointments booking URL</label>
                <input className="form-control mb-2" style={{ fontSize: 13 }} defaultValue={cfg.appointmentsUrl || ''}
                  placeholder="https://appointments.yourbrand.com"
                  onBlur={(e) => e.target.value !== (cfg.appointmentsUrl || '') && saveCfg({ appointmentsUrl: e.target.value })} />
                <label style={{ fontSize: 12, color: '#666' }}>Services (comma-separated)</label>
                <input className="form-control mb-2" style={{ fontSize: 13 }} defaultValue={(cfg.services || []).join(', ')}
                  placeholder="Book a demo, Contact us, Book a consultation"
                  onBlur={(e) => saveCfg({ services: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} />
                {cfg.widgetKey ? (
                  <div style={{ marginTop: 8 }}>
                    <label style={{ fontSize: 12, color: '#666' }}>Embed snippet</label>
                    <textarea className="form-control" readOnly rows={2} style={{ fontSize: 11, fontFamily: 'monospace' }} value={snippet} />
                    <button className="acc-btn acc-btn-primary mt-2" style={{ fontSize: 12 }}
                      onClick={() => { navigator.clipboard.writeText(snippet); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
                      {copied ? 'Copied!' : 'Copy embed snippet'}
                    </button>
                  </div>
                ) : (
                  <button className="acc-btn acc-btn-primary" style={{ fontSize: 12 }} disabled={saving} onClick={() => saveCfg({ enabled: true })}>
                    {saving ? 'Generating…' : 'Enable + generate widget key'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────── Meetings booking (calendar tab)
// Month grid (like the Posts/Ideas calendar) of booked consultations, plus a
// side list of the raw upcoming/pending requests. Bookings arrive once the
// appointments box calls back (Phase 3); until then the grid is simply empty.
function MeetingsTab({ brands }) {
  const [brandId, setBrandId] = useState('');
  const [meetings, setMeetings] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });

  const load = useCallback(async () => {
    const q = brandId ? `?brand_id=${encodeURIComponent(brandId)}` : '';
    try { const r = await get(`/api/lead-acquisition/forms/meetings${q}`); setMeetings((r && r.data) || []); }
    catch (e) { setMeetings([]); }
  }, [brandId]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const map = {};
    for (const mtg of meetings) {
      if (!mtg.appointmentAt) continue;
      const d = new Date(mtg.appointmentAt);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (map[key] = map[key] || []).push(mtg);
    }
    return map;
  }, [meetings]);

  const first = new Date(cursor.y, cursor.m, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(cursor.y, cursor.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const monthLabel = first.toLocaleString(undefined, { month: 'long', year: 'numeric' });
  const shift = (n) => setCursor((c) => { const d = new Date(c.y, c.m + n, 1); return { y: d.getFullYear(), m: d.getMonth() }; });

  return (
    <div>
      <div className="d-flex align-items-center mb-3" style={{ gap: 8 }}>
        <label style={{ fontSize: 12, color: '#666', margin: 0 }}>Brand</label>
        <select className="form-control" style={{ maxWidth: 240, fontSize: 13 }} value={brandId} onChange={(e) => setBrandId(e.target.value)}>
          <option value="">All brands</option>
          {(brands || []).map((b) => (<option key={b._id || b.id} value={b._id || b.id}>{b.name || b.brandName}</option>))}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="acc-btn" style={{ fontSize: 12 }} onClick={() => shift(-1)}>‹</button>
          <strong style={{ fontSize: 14 }}>{monthLabel}</strong>
          <button className="acc-btn" style={{ fontSize: 12 }} onClick={() => shift(1)}>›</button>
        </div>
      </div>

      <div className="sm-card">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4 }}>
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
            <div key={d} style={{ fontSize: 11, color: '#888', textAlign: 'center', fontWeight: 600, padding: 4 }}>{d}</div>
          ))}
          {cells.map((d, i) => {
            const key = d ? `${cursor.y}-${cursor.m}-${d}` : `x${i}`;
            const items = d ? (byDay[key] || []) : [];
            return (
              <div key={key} style={{ minHeight: 74, border: '1px solid #f1f1f1', borderRadius: 6, padding: 4, background: d ? '#fff' : '#fafafa' }}>
                {d && <div style={{ fontSize: 11, color: '#999' }}>{d}</div>}
                {items.map((m) => (
                  <div key={m._id} title={`${m.name || m.email} · ${m.service || ''}`}
                    style={{ fontSize: 10, background: '#eef4ff', color: '#0F62FE', borderRadius: 4, padding: '2px 4px', marginTop: 2, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {new Date(m.appointmentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} {m.name || m.email}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        {meetings.length === 0 && (
          <div className="sm-empty" style={{ marginTop: 10 }}>No booked meetings yet. Confirmed bookings from the appointments calendar appear here.</div>
        )}
      </div>
    </div>
  );
}

export default function LeadAcquisition() {
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux   = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux     = useSelector((s) => s.user.allPermission);
  const FinalPermissions = useMemo(() => (
    (allPermissionRedux || [])
      .filter((p) => (companyPermissionRedux || []).includes(p))
      .filter((p) => (usersPermissionRedux || []).includes(p))
  ), [allPermissionRedux, companyPermissionRedux, usersPermissionRedux]);
  const isSuperAdmin = !!userInfoRedux?.superAdmin;
  const has = (perm) => isSuperAdmin || FinalPermissions.includes(ALL_PERMISSIONS[perm]);

  const { tab: urlTab } = useParams();
  const history = useHistory();
  const visibleTabs = TABS.filter((t) => has(t.perm));
  const tab = (TABS.find((t) => t.key === urlTab) ? urlTab : (visibleTabs[0]?.key || 'crawler'));
  const setTab = (k) => history.push(`/lead-acquisition/${k}`);

  const [rows, setRows] = useState([]);
  const [leads, setLeads] = useState([]); // cached for the list-builder preview
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [creatingList, setCreatingList] = useState(false);
  const [filters, setFilters] = useState(EMPTY_SYSTEM_FILTERS);
  const brands = useBrandList();
  const visibleRows = useMemo(() => applySystemFilter(rows, filters), [rows, filters]);

  const activeTab = TABS.find((t) => t.key === tab);
  const refresh = useCallback(async () => {
    if (!activeTab) return;
    setBusy(true); setErr(null);
    try {
      const r = await get(activeTab.endpoint);
      setRows(((r && r.data) || []));
    } catch (e) { setErr((e && e.message) || 'Failed to load'); setRows([]); }
    finally { setBusy(false); }
  }, [activeTab]);

  useEffect(() => { refresh(); }, [refresh]);

  // The list builder previews matches against the lead pool — keep a copy
  // fresh whenever the leads tab loads or a list is about to be built.
  const loadLeads = useCallback(async () => {
    try { const r = await get('/api/lead-acquisition/leads'); setLeads(((r && r.data) || [])); } catch (e) {}
  }, []);
  useEffect(() => { if (tab === 'lists' || tab === 'leads') loadLeads(); }, [tab, loadLeads]);

  // Auto-poll while a crawl run is in flight so the tab-1 table reflects the
  // worker's progress (and the live elapsed timer) without a manual refresh.
  useEffect(() => {
    if (tab !== 'crawler') return undefined;
    const running = rows.some((r) => r.status === 'running');
    const t = setInterval(() => refresh(), running ? 4000 : 30000);
    return () => clearInterval(t);
  }, [tab, rows, refresh]);

  if (visibleTabs.length === 0) {
    return (
      <div className="container py-4">
        <div className="alert alert-warning">
          Your company doesn't have the Lead Acquisition package enabled. Ask a super admin to add it.
        </div>
      </div>
    );
  }

  const COLUMNS = {
    crawler: [['brandName', 'Brand'], ['status', 'Status'], ['__running', 'Running for'], ['sourcesChecked', 'Sources checked'], ['signalsUsed', 'Signals used'], ['totalCollected', 'Leads collected']],
    leads:   [['name', 'Name'], ['email', 'Email'], ['phone', 'Phone'], ['sources', 'Source(s)'], ['lastActiveAt', 'Last active'], ['mostPresentOn', 'Most present on'], ['status', 'Status']],
    lists:   [['name', 'Name'], ['leadCount', 'Leads'], ['contactsPushed', 'Pushed to CRM'], ['__emailList', 'Email list'], ['updatedAt', 'Updated']],
  };
  const cols = COLUMNS[tab] || [];

  const cell = (r, key) => {
    if (key === 'status') return statusBadge(r.status);
    if (key === '__running') {
      const start = r.startedAt ? new Date(r.startedAt).getTime() : null;
      if (!start) return '—';
      const end = r.status === 'running' ? Date.now() : (r.finishedAt ? new Date(r.finishedAt).getTime() : start + (r.durationMs || 0));
      return fmtDuration(end - start);
    }
    if (key === 'signalsUsed' || key === 'sourcesChecked') {
      const arr = r[key] || [];
      return arr.length ? `${arr.length}` + (arr.length ? ` (${arr.slice(0, 2).join(', ')}${arr.length > 2 ? '…' : ''})` : '') : '—';
    }
    if (key === '__emailList') return r.emailList_id ? '✓ in automations' : '—';
    return fmtCell(getPath(r, key), key);
  };

  const canBuildList = has('LEAD_LIST_ADD');

  return (
    <div className="container py-4">
      <style>{`
        .sm-tabs { display: flex; gap: 4px; border-bottom: 1px solid #e5e5e5; margin-bottom: 16px; }
        .sm-tab { padding: 8px 14px; cursor: pointer; border: 1px solid transparent; border-bottom: none; border-radius: 6px 6px 0 0; font-size: 13px; color: #525252; }
        .sm-tab.active { background: #fff; border-color: #e5e5e5; color: #15172b; font-weight: 600; }
        .sm-card { background: #fff; border: 1px solid #e5e5e5; border-radius: 8px; padding: 14px; }
        .sm-table { width: 100%; font-size: 13px; }
        .sm-table th { font-size: 11px; color: #888; text-transform: uppercase; text-align: left; font-weight: 500; border-bottom: 1px solid #f1f1f1; padding: 6px 8px; }
        .sm-table td { padding: 6px 8px; border-bottom: 1px solid #f4f4f4; }
        .sm-empty { color: #999; padding: 16px; text-align: center; }
      `}</style>

      <div className="d-flex align-items-baseline justify-content-between mb-2">
        <h3 className="mb-0">Lead Acquisition</h3>
        {tab === 'lists' && canBuildList && (
          <button className="acc-btn acc-btn-primary" onClick={() => { loadLeads(); setCreatingList(true); }}>+ New list</button>
        )}
      </div>

      <div className="sm-tabs">
        {visibleTabs.map((t) => (
          <div key={t.key} className={"sm-tab" + (tab === t.key ? " active" : "")} onClick={() => setTab(t.key)}>{t.label}</div>
        ))}
      </div>

      {tab === 'contact-forms' ? (
        <ContactFormsTab brands={brands} />
      ) : tab === 'meetings' ? (
        <MeetingsTab brands={brands} />
      ) : (
        <>
          <SystemFilterBar
            filters={filters}
            setFilters={setFilters}
            brands={brands}
            resultCount={visibleRows.length}
            totalCount={rows.length}
          />

          {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

          {tab === 'crawler' && has('LEAD_CRAWLER_RUN') && (
            <RunCrawler brands={brands} onRan={() => setTimeout(refresh, 800)} />
          )}

          <div className="sm-card">
            {busy && rows.length === 0 && <div className="sm-empty">Loading…</div>}
            {!busy && rows.length === 0 && <div className="sm-empty">No {activeTab.label.toLowerCase()} yet.</div>}
            {!busy && rows.length > 0 && visibleRows.length === 0 && <div className="sm-empty">No {activeTab.label.toLowerCase()} match the current filters.</div>}
            {visibleRows.length > 0 && (
              <table className="sm-table">
                <thead><tr>{cols.map(([key, label]) => (<th key={key}>{label}</th>))}</tr></thead>
                <tbody>
                  {visibleRows.map((r) => (
                    <tr key={r._id}>
                      {cols.map(([key]) => (<td key={key}>{cell(r, key)}</td>))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {creatingList && (
        <CreateListForm
          brands={brands}
          leads={leads}
          onClose={() => setCreatingList(false)}
          onCreated={() => { setCreatingList(false); refresh(); }}
        />
      )}
    </div>
  );
}
